import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChartBarIcon, XMarkIcon } from "@heroicons/react/24/outline";
import ApiCustom from "../../utils/ApiCustom";
import dayjs from "dayjs";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

// Animation config
const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

const ViewCountModal = ({
  isOpen,
  onClose,
  filterStart,
  filterEnd,
  setFilterStart,
  setFilterEnd,
  loading,
  chartData,
  totalView,
}) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        {...animation}
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
      >
        <motion.div
          {...animation}
          className="relative bg-white w-full max-w-4xl mx-2 rounded-2xl p-7 shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          <h2 className="text-xl font-bold  mb-2">
            Thống kê lượt truy cập website
          </h2>
          {/* Filter ngày */}
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="font-medium text-gray-500 text-sm">Từ</span>
            <input
              type="date"
              value={filterStart}
              max={filterEnd}
              onChange={(e) => setFilterStart(e.target.value)}
              className="border rounded-lg px-2 py-1 text-sm text-gray-600 bg-gray-50"
              style={{ minWidth: 120 }}
            />
            <span className="font-medium text-gray-500 text-sm">đến</span>
            <input
              type="date"
              value={filterEnd}
              min={filterStart}
              max={dayjs().format("YYYY-MM-DD")}
              onChange={(e) => setFilterEnd(e.target.value)}
              className="border rounded-lg px-2 py-1 text-sm text-gray-600 bg-gray-50"
              style={{ minWidth: 120 }}
            />
            <span className="ml-6 font-medium text-gray-500">Tổng lượt:</span>
            <span className="font-bold text-blue-700 text-xl">{totalView}</span>
          </div>
          {/* Chart */}
          <div>
            <div className="font-semibold text-blue-600 mb-2 flex items-center gap-2">
              <ChartBarIcon className="h-6 w-6" />
              Lượt truy cập theo thời gian
              {loading && (
                <span className="ml-2 animate-spin inline-block w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full"></span>
              )}
            </div>
            <div className="bg-gray-50 rounded-xl border px-2 py-3 min-h-[220px]">
              {chartData.length === 0 && !loading ? (
                <div className="text-gray-400 text-center py-8">
                  Không có dữ liệu
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={chartData}>
                    <XAxis dataKey="date" />
                    <YAxis width={50} />
                    <Tooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="count"
                      name="Lượt truy cập"
                      stroke="#3b82f6"
                      fill="#dbeafe"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const ViewCountCard = () => {
  const [isOpen, setIsOpen] = useState(false);
  // Filter ngày mặc định
  const defaultStart = dayjs().subtract(29, "day").format("YYYY-MM-DD");
  const defaultEnd = dayjs().format("YYYY-MM-DD");
  const [filterStart, setFilterStart] = useState(defaultStart);
  const [filterEnd, setFilterEnd] = useState(defaultEnd);

  // Data
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [totalView, setTotalView] = useState(0);

  // Tổng view cho card
  useEffect(() => {
    ApiCustom.get("/views/total")
      .then((res) => setTotalView(res.data?.data?.total || 0))
      .catch(() => setTotalView(0));
  }, []);

  // Fetch cho modal
  useEffect(() => {
    if (!isOpen) return;
    let ignore = false;
    const fetchData = async (from, to) => {
      setLoading(true);
      const countDays = dayjs(to).diff(dayjs(from), "day") + 1;
      const daysArr = Array.from({ length: countDays }, (_, i) =>
        dayjs(from).add(i, "day").format("YYYY-MM-DD"),
      );
      try {
        const res = await ApiCustom.get(`/views/range?from=${from}&to=${to}`);
        const apiData = res.data?.data || [];
        const merged = daysArr.map((date) => {
          const found = apiData.find(
            (d) => dayjs(d.date).format("YYYY-MM-DD") === date,
          );
          return { date, count: found ? found.count : 0 };
        });
        if (!ignore) {
          setChartData(merged);
          setTotalView(merged.reduce((sum, row) => sum + row.count, 0));
        }
      } catch {
        if (!ignore) {
          setChartData([]);
          setTotalView(0);
        }
      }
      if (!ignore) setLoading(false);
    };
    fetchData(filterStart, filterEnd);
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line
  }, [isOpen, filterStart, filterEnd]);

  return (
    <div className="w-full">
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="cursor-pointer transition-all p-5 rounded-2xl bg-white border border-blue-100 shadow-md"
        onClick={() => setIsOpen(true)}
      >
        <div className="flex flex-col items-center text-center gap-2">
          <div className="p-3 bg-blue-100 rounded-full mb-1 flex items-center justify-center">
            <ChartBarIcon className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <p className="text-black text-lg mb-1">
              Thông tin ghé thăm trang web
            </p>
            <p className="text-3xl font-bold text-black">{totalView}</p>
          </div>
        </div>
      </motion.div>
      {/* Modal chi tiết */}
      <ViewCountModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        filterStart={filterStart}
        filterEnd={filterEnd}
        setFilterStart={setFilterStart}
        setFilterEnd={setFilterEnd}
        loading={loading}
        chartData={chartData}
        totalView={totalView}
      />
    </div>
  );
};

export default ViewCountCard;
